import { Schema, SchemaGetter } from "effect";
import { ReviewRunState } from "../development/change-review/model.ts";

// These failures historically reach the generic internal-error HTTP boundary.
// Recovery uses the provider's statusCode, independently of this HTTP status.
const ReviewStateFields = /* @__PURE__ */ (() => Schema.Struct({
  message: Schema.String,
  status: Schema.tag(500),
  reviewId: Schema.String,
  observedState: Schema.optional(ReviewRunState)
}))();
type ReviewStateProperties = typeof ReviewStateFields["~type.make.in"];

export const SpecialistReviewStateError = /* @__PURE__ */ (() => {
  class SpecialistReviewStateError extends Schema.TaggedError<SpecialistReviewStateError>()(
    "SpecialistReviewStateError", ReviewStateFields
  ) {
    constructor(reviewId: string, message: string, observedState?: ReviewRunState);
    constructor(properties: ReviewStateProperties, options?: Schema.MakeOptions);
    constructor(reviewId: string | ReviewStateProperties, message?: string | Schema.MakeOptions, observedState?: ReviewRunState) {
      super(typeof reviewId === "string" ? {
        reviewId, message: typeof message === "string" ? message : "", observedState
      } : reviewId, typeof message === "object" ? message : undefined);
      this.name = "SpecialistReviewStateError";
    }

    toJSON() {
      return { name: this.name, reviewId: this.reviewId, observedState: this.observedState };
    }
  }
  return SpecialistReviewStateError;
})();
export type SpecialistReviewStateError = InstanceType<typeof SpecialistReviewStateError>;

export const FindingDispositionErrorCode = /* @__PURE__ */ (() => Schema.Literals([
  "assignment-required", "evidence-required", "insufficient-authority", "invalid-transition",
  "stale-authority", "stale-finding", "tampered-artifact"
]))();
export type FindingDispositionErrorCode = typeof FindingDispositionErrorCode.Type;
const DispositionFields = /* @__PURE__ */ (() => Schema.Struct({
  message: Schema.String,
  status: Schema.tag(500),
  code: FindingDispositionErrorCode
}))();
type DispositionProperties = typeof DispositionFields["~type.make.in"];

export const FindingDispositionError = /* @__PURE__ */ (() => {
  class FindingDispositionError extends Schema.TaggedError<FindingDispositionError>()(
    "FindingDispositionError", DispositionFields
  ) {
    constructor(code: FindingDispositionErrorCode, message: string);
    constructor(properties: DispositionProperties, options?: Schema.MakeOptions);
    constructor(code: FindingDispositionErrorCode | DispositionProperties, message?: string | Schema.MakeOptions) {
      super(typeof code === "string" ? {
        code, message: typeof message === "string" ? message : ""
      } : code, typeof message === "object" ? message : undefined);
      this.name = "FindingDispositionError";
    }

    toJSON() { return { name: this.name, code: this.code }; }
  }
  return FindingDispositionError;
})();
export type FindingDispositionError = InstanceType<typeof FindingDispositionError>;

export const SpecialistProviderErrorCode = /* @__PURE__ */ (() => Schema.Literals([
  "cancelled", "timeout", "rate-limited", "unavailable", "transport",
  "provider-authentication", "model-not-found", "invalid-request", "content-rejected"
]))();
export type SpecialistProviderErrorCode = typeof SpecialistProviderErrorCode.Type;
const PROVIDER_FAILURE_MESSAGE = "Specialist provider failed.";
// Local diagnostics retain the message. Explicit Schema encoding cannot persist
// provider prose, credentials, reasoning or arbitrary properties added upstream.
const ProviderMessage = /* @__PURE__ */ (() => Schema.String.pipe(Schema.decodeTo(Schema.String, {
  decode: SchemaGetter.passthrough(),
  encode: SchemaGetter.transform(() => PROVIDER_FAILURE_MESSAGE)
})))();
const ProviderFields = /* @__PURE__ */ (() => Schema.Struct({
  message: ProviderMessage,
  status: Schema.tag(500),
  code: SpecialistProviderErrorCode,
  // Invalid upstream numbers remain diagnosable locally; the recovery decoder
  // accepts only integer HTTP codes in 100..599, as before.
  statusCode: Schema.optional(Schema.Number)
}))();
type ProviderProperties = typeof ProviderFields["~type.make.in"];

/** A provider boundary error whose message is deliberately never persisted. */
export const SpecialistProviderError = /* @__PURE__ */ (() => {
  class SpecialistProviderError extends Schema.TaggedError<SpecialistProviderError>()(
    "SpecialistProviderError", ProviderFields
  ) {
    constructor(code: SpecialistProviderErrorCode, message?: string, statusCode?: number);
    constructor(properties: ProviderProperties, options?: Schema.MakeOptions);
    constructor(code: SpecialistProviderErrorCode | ProviderProperties, message?: string | Schema.MakeOptions, statusCode?: number) {
      super(typeof code === "string" ? {
        code, message: typeof message === "string" ? message : PROVIDER_FAILURE_MESSAGE, statusCode
      } : code, typeof message === "object" ? message : undefined);
      this.name = "SpecialistProviderError";
    }

    toJSON() { return { name: this.name, code: this.code, statusCode: this.statusCode }; }
  }
  return SpecialistProviderError;
})();
export type SpecialistProviderError = InstanceType<typeof SpecialistProviderError>;

const OutputFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(500) }))();
type OutputProperties = typeof OutputFields["~type.make.in"];

export const SpecialistOutputValidationError = /* @__PURE__ */ (() => {
  class SpecialistOutputValidationError extends Schema.TaggedError<SpecialistOutputValidationError>()(
    "SpecialistOutputValidationError", OutputFields
  ) {
    constructor(message: string | OutputProperties, options?: Schema.MakeOptions) {
      super(typeof message === "string" ? { message } : message, options);
      this.name = "SpecialistOutputValidationError";
    }

    toJSON() { return { name: this.name }; }
  }
  return SpecialistOutputValidationError;
})();
export type SpecialistOutputValidationError = InstanceType<typeof SpecialistOutputValidationError>;

export const SpecialistAttemptTimeout = /* @__PURE__ */ (() => {
  class SpecialistAttemptTimeout extends Schema.TaggedError<SpecialistAttemptTimeout>()(
    "SpecialistAttemptTimeout", { message: Schema.tag(""), status: Schema.tag(500) }
  ) {
    constructor(properties = {}, options?: Schema.MakeOptions) {
      super(properties, options);
      this.name = "Error";
    }

    toJSON() { return {}; }
  }
  return SpecialistAttemptTimeout;
})();
export type SpecialistAttemptTimeout = InstanceType<typeof SpecialistAttemptTimeout>;

export const SpecialistAttemptCancellation = /* @__PURE__ */ (() => {
  class SpecialistAttemptCancellation extends Schema.TaggedError<SpecialistAttemptCancellation>()(
    "SpecialistAttemptCancellation", { message: Schema.tag(""), status: Schema.tag(500) }
  ) {
    constructor(properties = {}, options?: Schema.MakeOptions) {
      super(properties, options);
      this.name = "Error";
    }

    toJSON() { return {}; }
  }
  return SpecialistAttemptCancellation;
})();
export type SpecialistAttemptCancellation = InstanceType<typeof SpecialistAttemptCancellation>;

export const ReviewFailure = /* @__PURE__ */ (() => Schema.Union([
  SpecialistReviewStateError, FindingDispositionError, SpecialistProviderError,
  SpecialistOutputValidationError, SpecialistAttemptTimeout, SpecialistAttemptCancellation
]))();
export type ReviewFailure = typeof ReviewFailure.Type;
